export interface HierarchyTreeItemRow<T> {
    ancestorRailDepths: number[];
    depth: number;
    firstSibling: boolean;
    hasChildren: boolean;
    hasFollowingSibling: boolean;
    item: T;
}

export const buildHierarchyTreeRows = <T>(
    items: readonly T[],
    getId: (item: T) => string,
    getParentId: (item: T) => string | undefined,
): HierarchyTreeItemRow<T>[] => {
    const itemById = new Map(items.map((item) => [getId(item), item]));
    const childrenByParent = new Map<string, T[]>();

    for (const item of items) {
        const parentId = getParentId(item);
        if (!parentId || parentId === getId(item) || !itemById.has(parentId)) {
            continue;
        }
        childrenByParent.set(parentId, [...(childrenByParent.get(parentId) ?? []), item]);
    }

    const rows: HierarchyTreeItemRow<T>[] = [];
    const visited = new Set<string>();
    const visit = (
        item: T,
        depth: number,
        hasFollowingSibling: boolean,
        ancestorRailDepths: number[] = [],
        firstSibling = true,
    ) => {
        const id = getId(item);
        if (visited.has(id)) {
            return;
        }
        visited.add(id);
        const children = childrenByParent.get(id) ?? [];
        rows.push({ ancestorRailDepths, depth, firstSibling, hasChildren: children.length > 0, hasFollowingSibling, item });
        const childAncestorRailDepths =
            depth > 0 && hasFollowingSibling ? [...ancestorRailDepths, depth - 1] : ancestorRailDepths;
        children.forEach((child, index) =>
            visit(child, depth + 1, index < children.length - 1, childAncestorRailDepths, index === 0),
        );
    };

    const roots = items.filter((item) => {
        const parentId = getParentId(item);
        return !parentId || !itemById.has(parentId) || parentId === getId(item);
    });
    roots.forEach((item, index) => visit(item, 0, index < roots.length - 1));
    items.forEach((item, index) => visit(item, 0, index < items.length - 1));

    return rows;
};

export const TWO_ROW_DROPDOWN_HIERARCHY_GEOMETRY = {
    anchorOffsetRem: 1,
    firstChildRailStartOffsetRem: 1.75,
    siblingRailStartOffsetRem: 2.25,
} as const;
